import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Tag, TagInsert, TagUpdate } from '../types/database'
import { Sidebar } from './Sidebar'

interface TagFormData {
    name: string
    color: string
    tag_type: string
}

export function TagManagement() {
    const [tags, setTags] = useState<Tag[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [editingTag, setEditingTag] = useState<Tag | null>(null)
    const [formData, setFormData] = useState<TagFormData>({
        name: '',
        color: '#3B82F6',
        tag_type: 'category'
    })

    const fetchTags = async () => {
        try {
            setLoading(true)
            setError('')

            const { data, error } = await supabase
                .from('tags')
                .select('*')
                .order('created_at', { ascending: false })

            if (error) throw error
            setTags(data || [])
        } catch (err) {
            setError(err instanceof Error ? err.message : '태그 목록을 불러오는데 실패했습니다.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTags()
    }, [])

    const filteredTags = tags.filter((tag) =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    )

    const handleCreate = async () => {
        try {
            setError('')
            const { error } = await supabase
                .from('tags')
                .insert([formData as TagInsert])

            if (error) throw error

            setShowCreateModal(false)
            setFormData({ name: '', color: '#3B82F6', tag_type: 'category' })
            fetchTags()
        } catch (err) {
            setError(err instanceof Error ? err.message : '태그 생성에 실패했습니다.')
        }
    }

    const handleUpdate = async () => {
        if (!editingTag) return

        try {
            setError('')
            const { error } = await supabase
                .from('tags')
                .update(formData as TagUpdate)
                .eq('id', editingTag.id)

            if (error) throw error

            setEditingTag(null)
            setFormData({ name: '', color: '#3B82F6', tag_type: 'category' })
            fetchTags()
        } catch (err) {
            setError(err instanceof Error ? err.message : '태그 수정에 실패했습니다.')
        }
    }

    const handleDelete = async (tag: Tag) => {
        if (!confirm(`"${tag.name}" 태그를 삭제하시겠습니까?`)) return

        try {
            setError('')
            const { error } = await supabase
                .from('tags')
                .delete()
                .eq('id', tag.id)

            if (error) throw error
            fetchTags()
        } catch (err) {
            setError(err instanceof Error ? err.message : '태그 삭제에 실패했습니다.')
        }
    }

    const openEditModal = (tag: Tag) => {
        setEditingTag(tag)
        setFormData({
            name: tag.name,
            color: tag.color,
            tag_type: tag.tag_type
        })
    }

    const closeModal = () => {
        setShowCreateModal(false)
        setEditingTag(null)
        setFormData({ name: '', color: '#3B82F6', tag_type: 'category' })
    }

    if (loading) {
        return (
            <div className="relative flex min-h-screen w-full bg-background-light font-display">
                <Sidebar />
                <main className="ml-64 flex flex-1 items-center justify-center">
                    <div className="flex items-center gap-2 text-gray-600">
                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                        <span className="text-lg">로딩 중...</span>
                    </div>
                </main>
            </div>
        )
    }

    return (
        <div className="relative flex min-h-screen w-full bg-background-light font-display">
            <Sidebar />
            <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
                <div className="w-full max-w-7xl mx-auto">
                    <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
                        <div>
                            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
                                태그 관리
                            </h1>
                            <p className="mt-1 text-sm text-gray-500">
                                문제 풀이에 사용할 태그를 관리합니다
                            </p>
                        </div>
                    </header>

                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                            <span className="material-symbols-outlined text-base">error</span>
                            <span className="text-sm">{error}</span>
                            <button
                                onClick={() => setError('')}
                                className="ml-auto text-red-600 hover:text-red-800"
                            >
                                <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                        </div>
                    )}

                    <div className="mb-6 flex items-center gap-4">
                        <div className="flex w-full flex-1 items-stretch rounded-lg h-10 bg-white border border-gray-200">
                            <div className="text-gray-400 flex items-center justify-center pl-3">
                                <span className="material-symbols-outlined">search</span>
                            </div>
                            <input
                                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 pl-2 text-sm"
                                placeholder="태그 이름으로 검색..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-2 h-10 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                        >
                            <span className="material-symbols-outlined text-sm">add</span>
                            <span className="text-sm font-medium">태그 추가</span>
                        </button>
                        <button
                            onClick={fetchTags}
                            className="flex items-center gap-2 h-10 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            <span className="material-symbols-outlined text-sm">refresh</span>
                            <span className="text-sm font-medium">새로고침</span>
                        </button>
                    </div>

                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        {filteredTags.length === 0 ? (
                            <div className="text-center py-12">
                                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">
                                    label
                                </span>
                                <p className="text-gray-500">
                                    {searchQuery ? '검색 결과가 없습니다.' : '태그가 없습니다.'}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                태그
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                색상
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                태그 타입
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                생성일
                                            </th>
                                            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                                작업
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {filteredTags.map((tag) => (
                                            <tr key={tag.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="h-[72px] px-4 py-2">
                                                    <span
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                                                        style={{ backgroundColor: tag.color }}
                                                    >
                                                        {tag.name}
                                                    </span>
                                                </td>
                                                <td className="h-[72px] px-4 py-2">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="w-6 h-6 rounded border border-gray-300"
                                                            style={{ backgroundColor: tag.color }}
                                                        />
                                                        <span className="text-sm text-gray-600 font-mono">{tag.color}</span>
                                                    </div>
                                                </td>
                                                <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                                                    {tag.tag_type}
                                                </td>
                                                <td className="h-[72px] px-4 py-2 text-sm text-gray-600">
                                                    {new Date(tag.created_at).toLocaleDateString('ko-KR')}
                                                </td>
                                                <td className="h-[72px] px-4 py-2 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => openEditModal(tag)}
                                                            className="p-2 text-gray-600 hover:text-primary hover:bg-primary-light rounded-lg transition-colors"
                                                        >
                                                            <span className="material-symbols-outlined text-sm">edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(tag)}
                                                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        >
                                                            <span className="material-symbols-outlined text-sm">delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <span className="material-symbols-outlined text-blue-600 text-base">info</span>
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">태그 안내</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li>태그는 문제 풀이를 분류하고 필터링하는데 사용됩니다</li>
                                    <li>태그 타입으로 용도를 구분할 수 있습니다 (예: status, category)</li>
                                    <li>색상을 지정하여 시각적으로 구분할 수 있습니다</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Create/Edit Modal */}
            {(showCreateModal || editingTag) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">
                            {editingTag ? '태그 수정' : '새 태그 추가'}
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    태그 이름
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                    placeholder="예: 피드백 필요"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    색상
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                                        placeholder="#3B82F6"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    태그 타입
                                </label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                    value={formData.tag_type}
                                    onChange={(e) => setFormData({ ...formData, tag_type: e.target.value })}
                                >
                                    <option value="category">카테고리</option>
                                    <option value="status">상태</option>
                                    <option value="priority">우선순위</option>
                                    <option value="other">기타</option>
                                </select>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-2">미리보기:</p>
                                <span
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white"
                                    style={{ backgroundColor: formData.color }}
                                >
                                    {formData.name || '태그 이름'}
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={closeModal}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                취소
                            </button>
                            <button
                                onClick={editingTag ? handleUpdate : handleCreate}
                                disabled={!formData.name}
                                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingTag ? '수정' : '추가'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
